import { RouterState } from "@/interfaces/Router";
import { auth } from "@/lib/firebase";
import {
  isEmailValid,
  isPasswordValid,
  isPhoneValid,
  onLoginSuccess,
  translateError,
} from "@/lib/utils";
import UserService from "@/services/user";
import { LOGIN_ROUTE } from "@/shared/constants";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Link,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import { createUserWithEmailAndPassword, updateProfile } from "firebase/auth";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import AuthWrapper from "./AuthWrapper";
import LoadingOverlay from "./LoadingOverlay";
import PasswordField from "./PasswordField";
import CustomPhoneInput from "./PhoneInput";

const SignUp = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { state } = useLocation() as { state: RouterState };
  const emailFromRouter = state?.email || "";

  // $ Получаем токен приглашения из URL параметров
  const [invitationToken, setInvitationToken] = useState<string | null>(null);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get("invitationToken");
    if (token) {
      setInvitationToken(token);
    }

    const refEmail = urlParams.get("refEmail");
    if (refEmail) {
      sessionStorage.setItem("refEmail", refEmail);
    }
  }, []);

  const [wasValidated, setWasValidated] = useState(false);
  const [email, setEmail] = useState(emailFromRouter);
  const [password, setPassword] = useState("");
  const [rePassword, setRePassword] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const [agreeToDataProcessing, setAgreeToDataProcessing] = useState(false);

  // error is ref, not state, because it's value shouldn't be changing together with inputs. And setWasValidated will trigger re-render anyway when error should be shown
  const errorRef = useRef<string | null>(null);
  const emailRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);
  const rePasswordRef = useRef<HTMLInputElement>(null);
  const nameRef = useRef<HTMLInputElement>(null);
  const phoneRef = useRef<HTMLInputElement>(null);

  const onError = (error: unknown) => {
    errorRef.current = translateError(error);
    console.error(error);

    // reset errors, since server return an error, any field could be invalid
    setWasValidated(false);
  };

  const createUserMutation = useMutation({
    mutationFn: async ({
      email,
      password,
      phone,
      name,
    }: {
      email: string;
      password: string;
      phone: string;
      name: string;
    }) => {
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        email,
        password
      );

      await updateProfile(userCredential.user, {
        displayName: name,
      });

      const idToken = await userCredential.user.getIdToken();
      await UserService.login({
        idToken,
        phone,
      });
    },
    onSuccess: () => {
      // $ Передаем токен приглашения в функцию onLoginSuccess
      onLoginSuccess(invitationToken);
    },
    onError,
  });

  const navigateTo = (
    event: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
    route: string
  ) => {
    event.preventDefault();

    const emailValue = emailRef.current?.value || "";
    const state: RouterState = { email: emailValue };

    // $ Добавляем токен к URL если он существует
    if (invitationToken) {
      navigate(`${route}?invitationToken=${invitationToken}`, {
        state,
      });
    } else {
      navigate(route, {
        state,
      });
    }
  };

  const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const emailValue = event.target.value;
    setEmail(emailValue);

    validateEmail(emailValue);
  };

  const validateEmail = (email: string): boolean => {
    if (!isEmailValid(email)) {
      emailRef.current?.setCustomValidity(t("errors.invalidEmail"));
      return true;
    } else {
      emailRef.current?.setCustomValidity("");
      return false;
    }
  };

  const handlePasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const passwordValue = event.target.value;
    setPassword(passwordValue);

    validatePassword(passwordValue);
    validatePasswordsEquality(passwordValue, rePassword);
  };

  const validatePassword = (password: string): boolean => {
    if (!isPasswordValid(password)) {
      passwordRef.current?.setCustomValidity(t("errors.invalidPassword"));
      return true;
    } else {
      passwordRef.current?.setCustomValidity("");
      return false;
    }
  };

  const handleRePasswordChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const rePasswordValue = event.target.value;
    setRePassword(rePasswordValue);

    validatePasswordsEquality(password, rePasswordValue);
  };

  const validatePasswordsEquality = (
    password: string,
    rePassword: string
  ): boolean => {
    if (password !== rePassword) {
      rePasswordRef.current?.setCustomValidity(t("errors.passwordMismatch"));
      return true;
    } else {
      rePasswordRef.current?.setCustomValidity("");
      return false;
    }
  };

  const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const nameValue = event.target.value;
    setName(nameValue);

    validateName(nameValue);
  };

  const handlePhoneChange = (phoneValue: string) => {
    setPhone(phoneValue);

    validatePhone(phoneValue);
  };

  const validatePhone = (phone: string): boolean => {
    if (!isPhoneValid(phone)) {
      phoneRef.current?.setCustomValidity(t("errors.invalidPhone"));
      return true;
    } else {
      phoneRef.current?.setCustomValidity("");
      return false;
    }
  };

  const validateName = (name: string): boolean => {
    if (!name) {
      nameRef.current?.setCustomValidity(t("errors.invalidName"));
      return true;
    } else {
      nameRef.current?.setCustomValidity("");
      return false;
    }
  };

  const validateTermsAgreement = (isChecked: boolean): boolean => {
    return !isChecked;
  };

  const validateDataProcessingAgreement = (isChecked: boolean): boolean => {
    return !isChecked;
  };

  const handleTermsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setAgreeToTerms(event.target.checked);
  };

  const handleDataProcessingChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setAgreeToDataProcessing(event.target.checked);
  };

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = async event => {
    event.preventDefault();
    errorRef.current = null;
    setWasValidated(true);

    const form = event.currentTarget;

    validateEmail(email);
    validatePassword(password);
    validatePasswordsEquality(password, rePassword);
    validateName(name);
    const hasTermsError = validateTermsAgreement(agreeToTerms);
    const hasDataProcessingError = validateDataProcessingAgreement(
      agreeToDataProcessing
    );

    if (!form.checkValidity() || hasTermsError || hasDataProcessingError) {
      return;
    }

    createUserMutation.mutate({
      email,
      password,
      phone,
      name,
    });
  };

  return (
    <AuthWrapper>
      <LoadingOverlay isLoading={createUserMutation.isPending} />

      <Box sx={{ width: "100%", maxWidth: 400 }}>
        {/* Header */}
        <Box sx={{ mb: 4, textAlign: "center" }}>
          <Typography variant="h3" sx={{ mb: 1, fontWeight: 700 }}>
            {t("signUp.title")}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {t("signUp.subtitle")}
          </Typography>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {t("shared.alreadyHaveAccount")}{" "}
              <Link
                component={RouterLink}
                to={`/${LOGIN_ROUTE}`}
                onClick={event => navigateTo(event, `/${LOGIN_ROUTE}`)}
                sx={{
                  textDecoration: "none",
                  fontWeight: 500,
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                {t("login.signIn")}
              </Link>
            </Typography>
          </Box>
        </Box>

        {/* Form */}
        <Box component="form" noValidate onSubmit={handleSubmit}>
          <Stack spacing={2}>
            {errorRef.current !== null && (
              <Alert severity="error" sx={{ borderRadius: 2 }}>
                {errorRef.current}
              </Alert>
            )}

            <TextField
              label={t("login.email")}
              id="email"
              type="email"
              inputRef={emailRef}
              value={email}
              onChange={handleEmailChange}
              variant="outlined"
              fullWidth
              error={wasValidated && emailRef.current?.validity.valid === false}
              helperText={wasValidated && emailRef.current?.validationMessage}
            />

            <TextField
              label={t("signUp.name")}
              id="username"
              type="text"
              value={name}
              onChange={handleNameChange}
              inputRef={nameRef}
              variant="outlined"
              fullWidth
              error={wasValidated && nameRef.current?.validity.valid === false}
              helperText={wasValidated && nameRef.current?.validationMessage}
            />

            <CustomPhoneInput
              value={phone}
              onChange={handlePhoneChange}
              label={t("signUp.phone")}
              error={wasValidated && phoneRef.current?.validity.valid === false}
              errorMessage={
                wasValidated ? phoneRef.current?.validationMessage : ""
              }
              ref={phoneRef}
            />

            <PasswordField
              label={t("shared.password")}
              id="password"
              value={password}
              onChange={handlePasswordChange}
              inputRef={passwordRef}
              error={
                wasValidated && passwordRef.current?.validity.valid === false
              }
              helperText={
                wasValidated && passwordRef.current?.validationMessage
              }
              externalShowPassword={showPassword}
              setExternalShowPassword={setShowPassword}
            />

            <PasswordField
              label={t("signUp.rePassword")}
              id="repassword"
              value={rePassword}
              onChange={handleRePasswordChange}
              inputRef={rePasswordRef}
              error={
                wasValidated && rePasswordRef.current?.validity.valid === false
              }
              helperText={
                wasValidated && rePasswordRef.current?.validationMessage
              }
              externalShowPassword={showPassword}
              setExternalShowPassword={setShowPassword}
            />

            <Box>
              <FormControl
                error={wasValidated && validateTermsAgreement(agreeToTerms)}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={agreeToTerms}
                      onChange={handleTermsChange}
                      color="primary"
                    />
                  }
                  label={
                    <Typography variant="body2" sx={{ pt: 0.3 }}>
                      {t("signUp.termsAgreementLabel")}
                    </Typography>
                  }
                  sx={{
                    alignItems: "flex-start",
                    "& .MuiCheckbox-root": {
                      paddingTop: 0,
                    },
                  }}
                />
                {wasValidated && validateTermsAgreement(agreeToTerms) && (
                  <FormHelperText>
                    {t("signUp.termsAgreementError")}
                  </FormHelperText>
                )}
              </FormControl>

              <FormControl
                error={
                  wasValidated &&
                  validateDataProcessingAgreement(agreeToDataProcessing)
                }
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={agreeToDataProcessing}
                      onChange={handleDataProcessingChange}
                      color="primary"
                    />
                  }
                  label={
                    <Typography variant="body2" sx={{ pt: 0.3 }}>
                      {t("signUp.dataProcessingPrefix")}{" "}
                      <Link
                        href="https://lotus1.org/content/blocks/files/personal_data.pdf"
                        target="_blank"
                        rel="noopener noreferrer"
                        color="primary"
                      >
                        {t("signUp.dataProcessingPersonalDataLink")}
                      </Link>
                      . {t("signUp.dataProcessingFamiliarWithPrefix")}{" "}
                      <Link
                        href="https://lotus1.org/content/blocks/files/personal_data.pdf"
                        target="_blank"
                        rel="noopener noreferrer"
                        color="primary"
                      >
                        {t("signUp.dataProcessingPolicyLink")}
                      </Link>
                    </Typography>
                  }
                  sx={{
                    alignItems: "flex-start",
                    "& .MuiCheckbox-root": {
                      paddingTop: 0,
                    },
                  }}
                />
                {wasValidated &&
                  validateDataProcessingAgreement(agreeToDataProcessing) && (
                    <FormHelperText>
                      {t("signUp.dataProcessingError")}
                    </FormHelperText>
                  )}
              </FormControl>
            </Box>

            <Box sx={{ pt: 2 }}>
              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                sx={{ py: 1.5 }}
              >
                {t("signUp.submit")}
              </Button>
            </Box>
          </Stack>
        </Box>
      </Box>
    </AuthWrapper>
  );
};

export default SignUp;
